import type { SVGProps } from 'react'

// ---------------------------------------------------------------------------
// Icon registry — paths ported from docs/design/saksbehandling-prototype/
// components.jsx. All icons share a 20×20 viewBox with currentColor stroke.
// Names are English identifiers (constitution II). The union type IconName
// is derived from the ICON_NAMES tuple so it is always in sync.
// ---------------------------------------------------------------------------

const PATHS = {
  inbox: (
    <>
      <path d="M3 12V5a2 2 0 012-2h10a2 2 0 012 2v7" />
      <path d="M3 12l3.6 5.4a1 1 0 00.83.6h5.14a1 1 0 00.83-.6L17 12" />
      <path d="M3 12h4.5l1 2h3l1-2H17" />
    </>
  ),
  folder: (
    <path d="M3 6a2 2 0 012-2h3.5l2 2H15a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
  ),
  search: (
    <>
      <circle cx="9" cy="9" r="5.5" />
      <path d="M17 17l-4-4" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a4 4 0 018 0v4l1.5 2.5h-11L6 12V8z" />
      <path d="M8 17a2 2 0 004 0" />
    </>
  ),
  settings: (
    <>
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 2v2M10 16v2M18 10h-2M4 10H2M15.5 4.5l-1.4 1.4M5.9 14.1l-1.4 1.4M15.5 15.5l-1.4-1.4M5.9 5.9L4.5 4.5" />
    </>
  ),
  plus: <path d="M10 4v12M4 10h12" />,
  grip: <g fill="currentColor" stroke="none"><circle cx="7" cy="5" r="1.25" /><circle cx="13" cy="5" r="1.25" /><circle cx="7" cy="10" r="1.25" /><circle cx="13" cy="10" r="1.25" /><circle cx="7" cy="15" r="1.25" /><circle cx="13" cy="15" r="1.25" /></g>,
  expand: <path d="M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4" />,
  collapse: <path d="M7 3v4H3M17 7h-4V3M13 17v-4h4M3 13h4v4" />,
  chevronD: <path d="M5 7l5 5 5-5" />,
  chevronR: <path d="M7 5l5 5-5 5" />,
  chevronL: <path d="M13 5l-5 5 5 5" />,
  check: <path d="M4 10l4 4 8-8" />,
  x: <path d="M5 5l10 10M15 5L5 15" />,
  clock: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 2" />
    </>
  ),
  user: (
    <>
      <circle cx="10" cy="7" r="3" />
      <path d="M3 17c1.5-3 4-4 7-4s5.5 1 7 4" />
    </>
  ),
  users: (
    <>
      <circle cx="7" cy="7" r="3" />
      <path d="M2 16c.8-2.5 3-3.5 5-3.5s4.2 1 5 3.5" />
      <path d="M13 8a3 3 0 003-3 3 3 0 00-3-3" />
      <path d="M18 16c-.6-2-2-3-3.5-3.3" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="3" width="12" height="14" rx="1" />
      <path d="M8 7h1M11 7h1M8 10h1M11 10h1M8 13h1M11 13h1" />
    </>
  ),
  doc: (
    <>
      <path d="M5 2h7l4 4v12a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z" />
      <path d="M12 2v4h4" />
    </>
  ),
  edit: <path d="M14 3l3 3-9 9H5v-3z" />,
  archive: (
    <>
      <path d="M3 5h14v3H3z" />
      <path d="M4 8v8a1 1 0 001 1h10a1 1 0 001-1V8" />
      <path d="M8 11h4" />
    </>
  ),
  chart: <path d="M3 17V8M8 17V4M13 17v-7M18 17v-5" />,
  flow: (
    <>
      <rect x="2" y="3" width="6" height="4" rx="1" />
      <rect x="12" y="3" width="6" height="4" rx="1" />
      <rect x="2" y="13" width="6" height="4" rx="1" />
      <rect x="12" y="13" width="6" height="4" rx="1" />
      <path d="M8 5h4M8 15h4M5 7v6M15 7v6" />
    </>
  ),
  book: (
    <>
      <path d="M3 4a2 2 0 012-2h11v14H5a2 2 0 00-2 2V4z" />
      <path d="M3 16a2 2 0 012-2h11" />
    </>
  ),
  filter: <path d="M3 5h14l-5 7v4l-4 1v-5L3 5z" />,
  sort: (
    <>
      <path d="M5 4v12M2 13l3 3 3-3" />
      <path d="M15 4v12M12 7l3-3 3 3" />
    </>
  ),
  download: (
    <>
      <path d="M10 3v10M5 9l5 4 5-4" />
      <path d="M3 16h14" />
    </>
  ),
  upload: (
    <>
      <path d="M10 14V4M5 8l5-4 5 4" />
      <path d="M3 16h14" />
    </>
  ),
  star: <path d="M10 2l2.5 5.2 5.5.8-4 4 1 5.7L10 15l-5 2.7 1-5.7-4-4 5.5-.8L10 2z" />,
  bolt: <path d="M11 2L3 12h5l-1 6 8-10h-5l1-6z" />,
  flag: (
    <>
      <path d="M4 2v16" />
      <path d="M4 3h11l-2 3 2 3H4" />
    </>
  ),
  link: (
    <>
      <path d="M8 11l-2 2a3 3 0 11-4-4l2-2" />
      <path d="M12 9l2-2a3 3 0 114 4l-2 2" />
      <path d="M7 13l6-6" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="14" height="10" rx="1" />
      <path d="M3 6l7 5 7-5" />
    </>
  ),
  phone: (
    <path d="M5 3h3l1.5 4-2 1.5a8 8 0 004 4L13 10.5l4 1.5v3a2 2 0 01-2 2A12 12 0 013 5a2 2 0 012-2z" />
  ),
  pin: (
    <>
      <path d="M10 17s-5-5-5-9a5 5 0 0110 0c0 4-5 9-5 9z" />
      <circle cx="10" cy="8" r="2" />
    </>
  ),
  home: (
    <>
      <path d="M3 9l7-6 7 6v8a1 1 0 01-1 1H4a1 1 0 01-1-1V9z" />
      <path d="M8 17v-5h4v5" />
    </>
  ),
  globe: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M3 10h14M10 3c2 2 3 4.3 3 7s-1 5-3 7M10 3c-2 2-3 4.3-3 7s1 5 3 7" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="11" y="3" width="6" height="6" rx="1" />
      <rect x="3" y="11" width="6" height="6" rx="1" />
      <rect x="11" y="11" width="6" height="6" rx="1" />
    </>
  ),
  list: (
    <>
      <path d="M6 5h11M6 10h11M6 15h11" />
      <circle cx="3" cy="5" r="1" />
      <circle cx="3" cy="10" r="1" />
      <circle cx="3" cy="15" r="1" />
    </>
  ),
  sun: (
    <>
      <circle cx="10" cy="10" r="3.5" />
      <path d="M10 2v2M10 16v2M18 10h-2M4 10H2M15.5 4.5l-1.4 1.4M5.9 14.1l-1.4 1.4M15.5 15.5l-1.4-1.4M5.9 5.9L4.5 4.5" />
    </>
  ),
  moon: (
    <path d="M16 11.5A7 7 0 018.5 4a7 7 0 105.6 11.4A7 7 0 0116 11.5z" />
  ),
  sparkle: (
    <>
      <path d="M10 2l1.4 4.6L16 8l-4.6 1.4L10 14l-1.4-4.6L4 8l4.6-1.4L10 2z" />
      <path d="M16 13l.7 1.8L18.5 15.5l-1.8.7L16 18l-.7-1.8L13.5 15.5l1.8-.7L16 13z" />
    </>
  ),
  paperclip: (
    <path d="M14 5l-7 7a3 3 0 104.2 4.2L18 9a5 5 0 10-7-7L4 9" />
  ),
  money: (
    <>
      <rect x="2" y="5" width="16" height="10" rx="1" />
      <circle cx="10" cy="10" r="2.5" />
      <path d="M5 8v.01M15 12v.01" />
    </>
  ),
  bank: (
    <>
      <path d="M2 7l8-4 8 4" />
      <path d="M3 7v10h14V7" />
      <path d="M6 17V9M10 17V9M14 17V9" />
    </>
  ),
  eye: (
    <>
      <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" />
      <circle cx="10" cy="10" r="2.5" />
    </>
  ),
  warn: (
    <>
      <path d="M10 2l8 14H2L10 2z" />
      <path d="M10 8v4M10 14v.01" />
    </>
  ),
  info: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v4M10 7v.01" />
    </>
  ),
  history: (
    <>
      <path d="M3 8V4M3 8h4" />
      <path d="M3 8a7 7 0 1 1-1 4" />
      <path d="M10 7v4l2.5 2" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="10" r="1" />
      <circle cx="10" cy="10" r="1" />
      <circle cx="15" cy="10" r="1" />
    </>
  ),
  pen: <path d="M14 3l3 3-9 9H5v-3z" />,
  refresh: (
    <>
      <path d="M3 10a7 7 0 0112-4l2 2M17 4v4h-4M17 10a7 7 0 01-12 4l-2-2M3 16v-4h4" />
    </>
  ),
  save: (
    <>
      <path d="M3 5a2 2 0 012-2h8l4 4v8a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
      <rect x="6" y="3" width="7" height="4" />
      <rect x="6" y="11" width="8" height="6" />
    </>
  ),
  play: <path d="M6 4l10 6-10 6V4z" />,
  print: (
    <>
      <path d="M5 8V3h10v5" />
      <rect x="3" y="8" width="14" height="7" rx="1" />
      <rect x="6" y="12" width="8" height="5" />
    </>
  ),
  template: (
    <>
      <rect x="3" y="3" width="14" height="14" rx="1" />
      <path d="M3 8h14M8 8v9" />
    </>
  ),
  git: (
    <>
      <circle cx="5" cy="5" r="2" />
      <circle cx="5" cy="15" r="2" />
      <circle cx="14" cy="10" r="2" />
      <path d="M5 7v6M7 15h3a2 2 0 002-2V12" />
    </>
  ),
  'arrow-right': <path d="M3 10h13M11 5l5 5-5 5" />,
  lock: (
    <>
      <rect x="4" y="9" width="12" height="8" rx="1.5" />
      <path d="M7 9V6.5a3 3 0 016 0V9" />
    </>
  ),
} as const

// Derive the IconName union type from the object's keys so the type and the
// runtime registry are always identical (single source of truth).
export type IconName = keyof typeof PATHS

// Exported as a const array for iteration (e.g. in tests and galleries).
export const ICON_NAMES = Object.keys(PATHS) as IconName[]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type IconProps = {
  name: IconName
  size?: number
  className?: string
} & Omit<SVGProps<SVGSVGElement>, 'name' | 'width' | 'height' | 'viewBox' | 'fill' | 'stroke'>

/**
 * Presentational icon wrapper. Renders an inline SVG using currentColor so
 * the icon inherits the surrounding text color. Defaults to aria-hidden=true
 * (decorative); pass aria-label for meaningful icons.
 *
 * Usage:
 *   <Icon name="search" />
 *   <Icon name="bell" size={24} aria-label="Varsler" />
 */
export function Icon({ name, size = 20, className, 'aria-label': ariaLabel, ...rest }: IconProps) {
  const content = PATHS[name] ?? null

  const a11yProps = ariaLabel
    ? { 'aria-label': ariaLabel, role: 'img' as const }
    : { 'aria-hidden': 'true' as const }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...a11yProps}
      {...rest}
    >
      {content}
    </svg>
  )
}
